import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { logEvent } from '~/config/firebase.config';
import { StripeServerForm } from '~/components/billing/StripeServerForm';
import type { PlanUpgrade } from '~/utils/planUpgrades';

import styles from './Account.redesign.module.css';

/**
 * Encart « Changer de formule » (rubrique Abonnement) : les formules au-dessus de la formule
 * actuelle (Standard à l'année, Pro au mois, Pro à l'année, cf. planUpgradesFor). Chaque bouton
 * ouvre directement l'écran de confirmation Stripe de cette formule (`intent`, stripe-b2b) : le
 * client vérifie le montant et confirme, c'est tout. Retour : `/account/annual` (« C'est fait »)
 * pour l'annuel, `/account/brand` (préparer son logo) pour le Pro.
 * `from` : formule actuelle, pour le texte du prélèvement (essai, mensuel, annuel).
 * Mesures : `account_upgrade_view` (`targets`), `account_upgrade_click` (`target`) ; l'annuel
 * garde aussi `account_annual_offer_view` / `_click` (suivi de l'offre annuelle depuis le 06/10).
 */
export function PlanUpgradesCard({
    clientID,
    upgrades,
    from,
    status,
}: {
    clientID: string;
    upgrades: PlanUpgrade[];
    from: 'monthly' | 'annual' | 'pro_monthly';
    status: string;
}) {
    const { t, i18n } = useTranslation();
    const trialing = status === 'trialing';
    const targets = upgrades.map((u) => u.target).join(',');

    useEffect(() => {
        logEvent('account_upgrade_view', 'Account', { clientID, targets, status });
        if (upgrades.some((u) => u.target === 'annual')) {
            logEvent('account_annual_offer_view', 'Account', { clientID, source: 'account', status });
        }
        // Une mesure par affichage de l'encart (pas à chaque relecture du statut).
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    let billing = 'account.upgrade.billingMonthly';
    if (trialing) billing = 'account.upgrade.billingTrial';
    else if (from === 'annual') billing = 'account.upgrade.billingAnnual';

    return (
        <div className={styles.annualOffer}>
            <p className={styles.annualTitle}>{t('account.upgrade.title')}</p>
            <p>{t(`account.upgrade.current.${from}`)}</p>
            <ul className={styles.upgradeList}>
                {upgrades.map((u) => (
                    <li key={u.target} className={styles.upgradeRow}>
                        <div className={styles.upgradeInfo}>
                            <span className={styles.upgradeName}>{t(`account.upgrade.${u.target}.name`)}</span>
                            <span className={styles.upgradePrice}>
                                {t(`account.upgrade.${u.target}.price`, { price: u.price })}
                            </span>
                            <span className={styles.upgradeDesc}>
                                {t(`account.upgrade.${u.target}.desc`, { twelveMonths: u.twelveMonths })}
                            </span>
                        </div>
                        <StripeServerForm
                            className={styles.inlineForm}
                            route="/create-customer-portal-session"
                            clientId={clientID}
                            lang={i18n.language}
                            intent={u.target}
                        >
                            <button
                                type="submit"
                                className={styles.saveBtn}
                                onClick={() => {
                                    logEvent('account_upgrade_click', 'Account', {
                                        clientID,
                                        target: u.target,
                                        status,
                                    });
                                    if (u.target === 'annual') {
                                        logEvent('account_annual_offer_click', 'Account', {
                                            clientID,
                                            source: 'account',
                                            status,
                                        });
                                    }
                                }}
                            >
                                {t('account.upgrade.cta')}
                            </button>
                        </StripeServerForm>
                    </li>
                ))}
            </ul>
            <p className={styles.annualSteps}>{t(billing)}</p>
        </div>
    );
}
